import React from 'react';
import {
    FormControl,
    TextField,
    MenuItem,
    FormControlLabel,
    Switch,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WorkflowSessionModalButton from './WorkflowSessionModalButton';
import SmartPasswordField from '../../../../../commonComponents/SmartPasswordField';

const GRANT_TYPES = [
    { value: 'AUTHORIZATION_CODE', labelKey: 'OAUTH2_GRANT_AUTH_CODE' },
    { value: 'AUTHORIZATION_CODE_PKCE', labelKey: 'OAUTH2_GRANT_AUTH_CODE_PKCE' },
    { value: 'IMPLICIT', labelKey: 'OAUTH2_GRANT_IMPLICIT' },
    { value: 'PASSWORD_CREDENTIALS', labelKey: 'OAUTH2_GRANT_PASSWORD_CREDENTIALS' },
    { value: 'CLIENT_CREDENTIALS', labelKey: 'OAUTH2_GRANT_CLIENT_CREDENTIALS' },
];

const CLIENT_AUTH_OPTIONS = [
    { value: 'SEND_IN_BODY', labelKey: 'OAUTH2_CLIENT_AUTH_SEND_IN_BODY' },
    { value: 'SEND_AS_BASIC_HEADER', labelKey: 'OAUTH2_CLIENT_AUTH_AS_BASIC' },
];

export const AddToOptions = [
    { value: 'REQUEST_HEADER', labelKey: 'OAUTH2_ADD_TO_REQUEST_HEADER' },
    { value: 'QUERY_PARAM', labelKey: 'OAUTH2_ADD_TO_QUERY_PARAM' },
];

const InputRow = ({
    t,
    labelKey,
    value,
    onChange,
    keyName,
    disableControl,
    screen,
    setWorkflowSessionModal,
    setValueLabel,
    onInputChange,
    setter,
    fontWeights,
    fontWeightKey,
    type = 'text',
    password = false,
    wizardLabelKey, // when set, use for workflow modal button (so valueLabel matches workflowMappings key)
}) => {
    const modalLabel = wizardLabelKey ? t(wizardLabelKey) : t(labelKey);
    const field = (
        <div style={{ display: 'flex', gap: '4px' }}>
            <FormControl className="width-100" size="small">
                {password ? (
                    <SmartPasswordField
                        variant="outlined"
                        size="small"
                        label={t(labelKey)}
                        value={value || ''}
                        disabled={disableControl || false}
                        inputProps={fontWeightKey && fontWeights ? { style: { fontWeight: fontWeights[fontWeightKey] } } : {}}
                        onChange={(e) => {
                            const v = e.target.value;
                            setter(v);
                            if (onChange) onChange(v);
                            else onInputChange(keyName, v);
                        }}
                        className="width-100"
                        name={keyName}
                        autoComplete="off"
                    />
                ) : (
                    <TextField
                        variant="outlined"
                        size="small"
                        label={t(labelKey)}
                        type={type}
                        value={value || ''}
                        disabled={disableControl || false}
                        inputProps={fontWeightKey && fontWeights ? { style: { fontWeight: fontWeights[fontWeightKey] } } : {}}
                        onChange={(e) => {
                            if (onChange) onChange(e.target.value);
                            else { setter(e.target.value); onInputChange(keyName, e.target.value); }
                        }}
                        className="width-100"
                        name={keyName}
                        autoComplete="off"
                    />
                )}
            </FormControl>
            {['GET_DATA', 'SEND_DATA'].includes(screen) && (
                <WorkflowSessionModalButton
                    disableControl={disableControl || false}
                    label={modalLabel}
                    setWorkflowSessionModal={setWorkflowSessionModal}
                    setValueLabel={setValueLabel}
                />
            )}
        </div>
    );
    return field;
};

const OAuth2Fields = (props) => {
    const {
        t,
        disableControl,
        screen,
        onInputChange,
        setWorkflowSessionModal,
        setValueLabel,
        fontWeights = {},
        oauth2Token,
        setOauth2Token,
        oauth2HeaderPrefix,
        setOauth2HeaderPrefix,
        oauth2AutoRefresh,
        setOauth2AutoRefresh,
        oauth2AddTo,
        setOauth2AddTo,
        oauth2GrantType,
        setOauth2GrantType,
        oauth2AccessTokenUrl,
        setOauth2AccessTokenUrl,
        oauth2ClientId,
        setOauth2ClientId,
        oauth2ClientSecret,
        setOauth2ClientSecret,
        oauth2Username,
        setOauth2Username,
        oauth2Password,
        setOauth2Password,
        onOauth2PasswordChange,
        oauth2ClientAuth,
        setOauth2ClientAuth,
        oauth2Scope,
        setOauth2Scope,
        oauth2State,
        setOauth2State,
        oauth2RefreshTokenUrl,
        setOauth2RefreshTokenUrl,
    } = props;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <InputRow
                t={t}
                labelKey="OAUTH2_TOKEN"
                value={oauth2Token}
                keyName="OAUTH2_TOKEN"
                disableControl={disableControl}
                screen={screen}
                setWorkflowSessionModal={setWorkflowSessionModal}
                setValueLabel={setValueLabel}
                onInputChange={onInputChange}
                setter={setOauth2Token}
            />
            
            <InputRow
                t={t}
                labelKey="OAUTH2_HEADER_PREFIX"
                value={oauth2HeaderPrefix}
                keyName="OAUTH2_HEADER_PREFIX"
                disableControl={disableControl}
                screen={screen}
                setWorkflowSessionModal={setWorkflowSessionModal}
                setValueLabel={setValueLabel}
                onInputChange={onInputChange}
                setter={setOauth2HeaderPrefix}
            />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span style={{ fontSize: '0.875rem' }}>{t('OAUTH2_AUTO_REFRESH_TOKEN')}</span>
                <Switch
                    checked={!!oauth2AutoRefresh}
                    disabled={disableControl || false}
                    onChange={(e) => setOauth2AutoRefresh(e.target.checked)}
                    color="primary"
                    size="small"
                />
            </div>

            <Accordion defaultExpanded disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">{t('OAUTH2_CONFIGURE_TOKEN')}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl className="width-100" size="small">
                        <TextField
                            select
                            variant="outlined"
                            size="small"
                            label={t('OAUTH2_GRANT_TYPE')}
                            value={oauth2GrantType || 'PASSWORD_CREDENTIALS'}
                            disabled={disableControl || false}
                            onChange={(e) => { setOauth2GrantType(e.target.value); onInputChange('OAUTH2_GRANT_TYPE', e.target.value); }}
                            className="width-100"
                        >
                            {GRANT_TYPES.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</MenuItem>
                            ))}
                        </TextField>
                    </FormControl>

                    <InputRow
                        t={t}
                        labelKey="OAUTH2_ACCESS_TOKEN_URL"
                        value={oauth2AccessTokenUrl}
                        keyName="OAUTH2_ACCESS_TOKEN_URL"
                        disableControl={disableControl}
                        screen={screen}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                        onInputChange={onInputChange}
                        setter={setOauth2AccessTokenUrl}
                    />

                    <InputRow
                        t={t}
                        labelKey="LBL_CLIENT_ID"
                        value={oauth2ClientId}
                        keyName="OAUTH2_CLIENT_ID"
                        disableControl={disableControl}
                        screen={screen}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                        onInputChange={onInputChange}
                        setter={setOauth2ClientId}
                    />
                    
                    <InputRow
                        t={t}
                        labelKey="LBL_CLIENT_SECRET"
                        value={oauth2ClientSecret}
                        keyName="OAUTH2_CLIENT_SECRET"
                        disableControl={disableControl}
                        screen={screen}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                        onInputChange={onInputChange}
                        setter={setOauth2ClientSecret}
                    />
                    
                    <InputRow
                        t={t}
                        labelKey="LBL_USERNAME"
                        value={oauth2Username}
                        keyName="OAUTH2_USERNAME"
                        disableControl={disableControl}
                        screen={screen}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                        onInputChange={onInputChange}
                        setter={setOauth2Username}
                        wizardLabelKey="OAUTH2_USERNAME_LBL"
                    />
                    
                    <InputRow
                        t={t}
                        labelKey="LBL_WS_PASSWORD"
                        value={oauth2Password}
                        keyName="OAUTH2_PASSWORD"
                        disableControl={disableControl}
                        screen={screen}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                        onInputChange={onInputChange}
                        setter={setOauth2Password}
                        onChange={onOauth2PasswordChange}
                        password
                        wizardLabelKey="OAUTH2_PASSWORD_LBL"
                    />
                    
                    <FormControl className="width-100" size="small">
                        <TextField
                            select
                            variant="outlined"
                            size="small"
                            label={t('OAUTH2_CLIENT_AUTHENTICATION')}
                            value={oauth2ClientAuth || ''}
                            disabled={disableControl || false}
                            onChange={(e) => { setOauth2ClientAuth(e.target.value); onInputChange('OAUTH2_CLIENT_AUTHENTICATION', e.target.value); }}
                            className="width-100"
                        >
                            {CLIENT_AUTH_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</MenuItem>
                            ))}
                        </TextField>
                    </FormControl>
                </AccordionDetails>
            </Accordion>

            <Accordion disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">{t('OAUTH2_ADVANCED')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <InputRow
                        t={t}
                        labelKey="OAUTH2_REFRESH_TOKEN_URL"
                        value={oauth2RefreshTokenUrl}
                        keyName="OAUTH2_REFRESH_TOKEN_URL"
                        disableControl={disableControl}
                        screen={screen}
                        setWorkflowSessionModal={setWorkflowSessionModal}
                        setValueLabel={setValueLabel}
                        onInputChange={onInputChange}
                        setter={setOauth2RefreshTokenUrl}
                    />
                </AccordionDetails>
            </Accordion>
        </div>
    );
};

export default OAuth2Fields;
